import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { supabase } from '@/lib/supabaseClient';
import { fetchSceneDefs, sceneStage, adminPathForScene, samePath, PART_META, isEyesUpScene, sceneImage, sceneTheme } from '@/lib/demoScenes';
import { postStage, subscribeStage, markProjectorWindow, isProjectorWindow } from '@/lib/stageChannel';

// Kept exported from here: ProjectorStagePage and others import these names
// from this file; they now live in stageChannel (see the note there).
export { markProjectorWindow, isProjectorWindow };
import { CurtainFace, fetchCurtainMedia } from '@/components/admin/ScreenCurtain';
import OpeningSlide from '@/components/admin/OpeningSlide';
import FieldAssistScanWall from '@/components/admin/FieldAssistScanWall';
import { MonitorPlay, Presentation } from 'lucide-react';

// ── Presentation stage ───────────────────────────────────────────────────────
// What the ROOM sees, in both setups, driven by one per-scene flag (show_live).
//
//   Second display  the presenter opens the projector window and drags it onto
//                   the TV. On a 'live' scene that window navigates to the
//                   scene's admin page, so the room sees the real product; on a
//                   'backdrop' scene it returns to the deck's scene card. The
//                   presenter's own laptop is never touched.
//
//   Mirrored screen there is only one screen, so the room sees exactly what the
//                   presenter sees. Mirroring mode covers this screen with the
//                   scene backdrop and lifts it for 'live' scenes. The presenter
//                   sees the backdrop too — unavoidable on one screen — and
//                   drives from the nav pad, which floats above it.
//
// The scene → page mapping is NOT decided here: sceneStage() and
// adminPathForScene() in src/lib/demoScenes.js own it, so the projector and the
// backdrop can never disagree about what a scene means.

const POLL_MS = 6000;
const MIRROR_KEY = 'pl_demo_mirror';
const MIRROR_EVENT = 'parcelink:mirror';
const BEAT_MS = 3000;

// ── Mirroring mode (per device — it describes THIS screen, not the session) ──
export const isMirroring = () => {
  try { return localStorage.getItem(MIRROR_KEY) === '1'; } catch { return false; }
};
export const setMirroring = (on) => {
  try { localStorage.setItem(MIRROR_KEY, on ? '1' : '0'); } catch { /* private mode */ }
  window.dispatchEvent(new CustomEvent(MIRROR_EVENT, { detail: { on: !!on } }));
};
export const toggleMirroring = () => setMirroring(!isMirroring());
export const subscribeMirroring = (fn) => {
  const handler = (e) => fn(!!e?.detail?.on);
  window.addEventListener(MIRROR_EVENT, handler);
  return () => window.removeEventListener(MIRROR_EVENT, handler);
};

/** The live session and its current scene. Polled — a show_live toggle mid-
 *  presentation has to reach the room without a reload. */
export function useLiveDemoScene() {
  const [session, setSession] = useState(null);
  const [scenes, setScenes] = useState([]);

  useEffect(() => {
    let stop = false;
    const load = async () => {
      try {
        const { data } = await supabase
          .from('demo_sessions')
          .select('id, code, title, status, current_scene')
          .neq('status', 'ended')
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (!stop) setSession(data || null);
      } catch { /* keep the last known state rather than blanking the room */ }
    };
    const channel = supabase
      .channel('presentation-stage')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'demo_sessions' }, load)
      .subscribe();
    load();
    const id = setInterval(load, POLL_MS);
    return () => { stop = true; clearInterval(id); supabase.removeChannel(channel); };
  }, []);

  // Scenes are re-read on the same beat: the per-scene "show my real screen"
  // tick must take effect during a presentation, not on the next reload —
  // and INSTANTLY when a control announces it ('scenes-refresh' on the stage
  // channel), because a Screen press that visibly does nothing for six
  // seconds reads as broken (owner, 22 Sep).
  useEffect(() => {
    let stop = false;
    const load = () => fetchSceneDefs().then((l) => { if (!stop) setScenes(l); }).catch(() => {});
    load();
    const id = setInterval(load, POLL_MS);
    const unsub = subscribeStage((msg) => { if (msg?.type === 'scenes-refresh') load(); });
    return () => { stop = true; clearInterval(id); unsub(); };
  }, []);

  const idx = useMemo(() => {
    if (scenes.length === 0) return 0;
    return Math.max(0, Math.min(scenes.length - 1, session?.current_scene ?? 0));
  }, [scenes, session?.current_scene]);

  const scene = scenes[idx] || null;
  return { session, scenes, scene, idx, stage: sceneStage(scene) };
}

/** The slide image on a scene card — admin-chosen (or the page's stored
 *  capture), with the designer's motion and container. Hides itself when the
 *  image doesn't exist. Shared by the mirrored backdrop and the projector. */
export const SceneCardImage = ({ scene }) => {
  const [failed, setFailed] = useState(false);
  const slide = sceneImage(scene);
  const t = sceneTheme(scene);
  useEffect(() => { setFailed(false); }, [slide.src]);
  if (!slide.src || failed) return null;
  return (
    <motion.div
      animate={slide.motion === 'still' ? {}
        : slide.motion === 'drift' ? { scale: [1.04, 1.1], x: ['-1%', '1%'] }
        : { y: [0, -8, 0] }}
      transition={slide.motion === 'drift'
        ? { duration: 20, repeat: Infinity, repeatType: 'reverse', ease: 'linear' }
        : { duration: 6, repeat: Infinity, ease: 'easeInOut' }}
      className={slide.frame === 'full'
        ? `mx-auto mt-8 max-h-[34vh] w-full max-w-3xl overflow-hidden rounded-3xl border shadow-2xl ${t.frame}`
        : `mx-auto mt-8 max-h-[34vh] overflow-hidden rounded-3xl border-2 shadow-2xl ${t.frame}`}
    >
      <img
        src={slide.src}
        alt=""
        className={slide.frame === 'full' ? 'block w-full object-cover' : 'block w-[220px]'}
        onError={() => setFailed(true)}
      />
    </motion.div>
  );
};

/** The scene card the room reads while the real screen stays hidden. */
const SceneBackdrop = ({ scene, idx, total }) => {
  const part = PART_META[scene?.part_key];
  const t = sceneTheme(scene);
  return (
    <div className={`flex h-full w-full flex-col items-center justify-center px-8 text-center ${t.stage}`}>
      <p className="mb-3 text-xs font-bold uppercase tracking-[0.3em] text-primary/80">
        Scene {idx + 1} / {total}
      </p>
      <h1 className={`max-w-4xl text-3xl font-semibold leading-tight tracking-tight md:text-5xl ${t.heading}`}>
        {scene?.title || 'Parcelink'}
      </h1>
      {part && (
        <p className="mt-5 rounded-full border border-primary/40 bg-primary/10 px-4 py-1.5 text-sm font-semibold text-primary">
          {part.name} · {part.van}
        </p>
      )}
      {scene?.hint && (
        <p className={`mt-5 max-w-2xl text-base md:text-lg ${t.body}`}>{scene.hint}</p>
      )}
      <SceneCardImage scene={scene} />
      {isEyesUpScene(scene) && (
        <p className={`mt-8 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] ${t.faint}`}>
          <MonitorPlay className="h-4 w-4" /> Eyes up
        </p>
      )}
    </div>
  );
};

/**
 * Mirrored-screen backdrop. Covers THIS screen during a presentation and lifts
 * for scenes ticked "show my real screen".
 *
 * Sits at z-60, below the nav pad's z-70, because the presenter still has to
 * drive the deck from on top of it — that is the point of the backdrop rather
 * than the hard curtain, which covers everything.
 *
 * Never shows unless mirroring mode is deliberately switched on, so an admin
 * doing ordinary work while a session happens to be live is never blinded. Esc
 * peeks until the next scene; the pill exits mirroring outright.
 */
export const PresentationBackdrop = () => {
  const { session, scenes, scene, idx, stage } = useLiveDemoScene();
  const [mirror, setMirror] = useState(isMirroring);
  const [peek, setPeek] = useState(false);
  // Owner rule (21 Sep): between live pages the screen rests on the CURTAIN —
  // the personalised film when one exists — with the nav pad floating above
  // it (pad z-70 > this z-60), so the presenter drives scenes from the
  // curtain. The plain scene card remains the fallback when no curtain media
  // exists at all.
  const [media, setMedia] = useState(null);

  useEffect(() => subscribeMirroring(setMirror), []);
  useEffect(() => { setPeek(false); }, [idx]); // a new scene re-covers the screen

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setPeek((p) => !p); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // The projector window never wears the backdrop: it IS the room's screen and
  // renders the deck itself. In the LOBBY the backdrop is the opening slide —
  // mirrored rooms need somewhere to scan from too (owner, 1 Oct).
  const lobby = session?.status === 'lobby';
  const show = mirror && !!session && !isProjectorWindow() && (lobby || stage === 'backdrop') && !peek;

  useEffect(() => {
    if (!show) return undefined;
    let stale = false;
    fetchCurtainMedia().then((m) => { if (!stale) setMedia(m); });
    return () => { stale = true; };
  }, [show, session?.id]);

  const curtainLook = !!(media && (media.video || media.still));

  return (
    <>
      <AnimatePresence>
        {show && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-slate-950"
            role="img"
            aria-label={`Presentation backdrop — scene ${idx + 1}: ${scene?.title || ''}`}
          >
            {lobby ? (
              <OpeningSlide session={session} />
            ) : scene?.part_key === 'field-assist' ? (
              <>
                <FieldAssistScanWall sessionId={session?.id} />
                <div className="pointer-events-none absolute left-1/2 top-5 -translate-x-1/2 rounded-full bg-black/45 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.25em] text-white/85 backdrop-blur-sm">
                  Scene {idx + 1} / {scenes.length || 1} · {scene?.title || ''}
                </div>
              </>
            ) : curtainLook ? (
              <>
                <CurtainFace media={media} />
                {/* A quiet position chip so the presenter never loses their
                    place while resting on the curtain. */}
                <div className="pointer-events-none absolute left-1/2 top-5 -translate-x-1/2 rounded-full bg-black/45 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.25em] text-white/85 backdrop-blur-sm">
                  Scene {idx + 1} / {scenes.length || 1} · {scene?.title || ''}
                </div>
              </>
            ) : (
              <SceneBackdrop scene={scene} idx={idx} total={scenes.length || 1} />
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Always reachable while mirroring, so nobody is ever stuck behind the
          backdrop — including when the deck or the network misbehaves. NEVER on
          the projector window: mirroring is a per-profile localStorage flag, so
          the projector shares it, and the room must not see presenter chrome
          (3 Oct, pre-presentation audit). */}
      {mirror && !isProjectorWindow() && (
        <button
          type="button"
          onClick={() => setMirroring(false)}
          className="fixed right-4 top-4 z-[80] flex items-center gap-2 rounded-full border border-primary/40 bg-slate-950/90 px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest text-white shadow-2xl backdrop-blur-md hover:bg-slate-900"
        >
          <Presentation className="h-3.5 w-3.5 text-primary" /> Mirroring · exit
        </button>
      )}
    </>
  );
};

/**
 * Drives the projector WINDOW, and only that window.
 *
 * One emitter, in the admin window, posts the stage for the current scene; the
 * projector window acts on it. Emitting from a single place — rather than from
 * the console and the nav pad separately — is what guarantees the room follows
 * whichever control the presenter actually used.
 */
export const ProjectorStageDriver = () => {
  const { session, scene, idx, stage } = useLiveDemoScene();
  const navigate = useNavigate();
  const location = useLocation();
  const projector = isProjectorWindow();
  const beat = useRef(null);
  const lastSent = useRef('');
  const lastStage = useRef(null);

  // Projector window: keep the heartbeat alive wherever it has navigated to,
  // and follow the stage.
  useEffect(() => {
    if (!projector) return undefined;
    postStage({ type: 'hello' });
    beat.current = setInterval(() => postStage({ type: 'beat' }), BEAT_MS);
    const unsub = subscribeStage((msg) => {
      if (msg?.type !== 'stage') return;
      const target = msg.mode === 'live' && msg.path ? msg.path : '/admin/projector';
      // Compare pathname only: an admin link may carry a query (the van lab's
      // ?showreel=1), and matching the full string would re-navigate on every
      // heartbeat — restarting the showreel forever.
      if (!samePath(window.location.pathname, target)) navigate(target);
    });
    return () => { clearInterval(beat.current); unsub(); };
  }, [projector, navigate]);

  // Admin window: announce the current scene's stage whenever it changes.
  // In the LOBBY the projector must stay on the opening slide — a deck whose
  // first scene auto-goes-live would otherwise drag the window off the QR
  // before anyone has scanned it.
  useEffect(() => {
    if (projector || !session || !scene) return;
    const effStage = session.status === 'lobby' ? 'backdrop' : stage;
    const path = effStage === 'live' ? adminPathForScene(scene) : null;
    const key = `${idx}|${effStage}|${path || ''}`;
    if (lastSent.current === key) return;
    lastSent.current = key;
    lastStage.current = { type: 'stage', mode: effStage, path, scene: idx, title: scene.title || '' };
    postStage(lastStage.current);
  }, [projector, session, scene, idx, stage]);

  // A projector opened (or reloaded) MID-SCENE says hello; answer with the
  // current stage so it catches up instead of sitting on the deck card until
  // the next scene change (pre-presentation audit, 14 Oct).
  useEffect(() => {
    if (projector) return undefined;
    return subscribeStage((msg) => {
      if (msg?.type === 'hello' && lastStage.current) postStage(lastStage.current);
    });
  }, [projector]);

  // A ribbon so the room always knows what it is looking at while the projector
  // is showing a real admin page.
  if (!projector || stage !== 'live' || location.pathname === '/admin/projector') return null;
  return (
    <div className="fixed inset-x-0 top-0 z-[120] flex items-center justify-center gap-3 bg-slate-950/95 px-4 py-2 text-white backdrop-blur-md">
      <span className="text-[11px] font-bold uppercase tracking-[0.25em] text-primary">Scene {idx + 1}</span>
      <span className="truncate text-sm font-semibold">{scene?.title}</span>
    </div>
  );
};

export default PresentationBackdrop;
